import { useEffect, useRef } from "react";
import {
  getThreadDetailId,
  sameThreadId,
  threadHasMessage,
} from "../../providers/thread-detail-state";

const PAGE_SIZE = 30;

// A notification is an invalidation, not a complete message (its body is
// truncated). Keep the selected conversation dirty until the API catches up.
export default function useThreadReconciliation({
  selectedThreadId,
  threadDetail,
  personalInboxId,
  legacy,
  notification,
  getPaginatedThreadDetail,
  getPersonalPaginatedThreadDetail,
}) {
  const latest = useRef();
  latest.current = {
    threadDetail,
    getPaginatedThreadDetail,
    getPersonalPaginatedThreadDetail,
  };
  const controller = useRef(null);
  const lastNotification = useRef(notification);
  const scopeThreadId = selectedThreadId > 0 ? String(selectedThreadId) : null;
  const readyThreadId =
    selectedThreadId > 0 &&
    sameThreadId(getThreadDetailId(threadDetail), selectedThreadId)
      ? String(selectedThreadId)
      : null;

  useEffect(() => {
    if (!scopeThreadId) return;

    let active = true;
    let inFlight = false;
    let dirty = false;
    let retryTimer;
    let retryDelay = 750;
    const pendingIds = new Set();
    const isActive = () => active;

    const reconcile = async () => {
      dirty = true;
      if (!active || inFlight || document.visibilityState === "hidden") return;
      if (!sameThreadId(
        getThreadDetailId(latest.current.threadDetail), scopeThreadId,
      )) return;
      window.clearTimeout(retryTimer);
      inFlight = true;
      dirty = false;
      let succeeded = false;
      // If more than one page arrived while disconnected, bridge the gap to
      // the messages already loaded instead of silently dropping the middle.
      const knownIds = new Set(
        (latest.current.threadDetail?.messages || [])
          .filter((message) => message.message_id != null)
          .map((message) => String(message.message_id)),
      );

      try {
        let offset = 0;
        let more;
        do {
          const actions = latest.current;
          const thread =
            personalInboxId !== null && !legacy
              ? await actions.getPersonalPaginatedThreadDetail(
                  scopeThreadId,
                  personalInboxId,
                  offset,
                  PAGE_SIZE,
                  isActive,
                )
              : await actions.getPaginatedThreadDetail(
                  scopeThreadId, offset, PAGE_SIZE, isActive,
                );
          if (!active) return;
          if (!sameThreadId(getThreadDetailId(thread), scopeThreadId)) return;

          const messages = thread.messages || [];
          messages.forEach((message) =>
            pendingIds.delete(String(message.message_id)),
          );
          more =
            knownIds.size > 0 && messages.length === PAGE_SIZE &&
            !messages.some((message) => knownIds.has(String(message.message_id)));
          offset += messages.length;
        } while (more && active);
        succeeded = true;
      } catch (error) {
        // Leave this conversation dirty. Recovery must survive a transient
        // failure without requiring another notification or a page reload.
      } finally {
        inFlight = false;
        if (active && (dirty || pendingIds.size > 0 || !succeeded)) {
          retryTimer = window.setTimeout(reconcile, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 30000);
        } else {
          retryDelay = 750;
        }
      }
    };

    controller.current = {
      reconcile,
      notify: (message) => {
        if (!sameThreadId(message?.thread_id, scopeThreadId)) return;
        if (
          personalInboxId !== null && message.personal_inbox_id != null &&
          !sameThreadId(message.personal_inbox_id, personalInboxId)
        ) return;
        if (
          message.message_id != null &&
          !threadHasMessage(latest.current.threadDetail, message.message_id)
        ) {
          pendingIds.add(String(message.message_id));
        }
        reconcile();
      },
    };
    const whenVisible = () => {
      if (document.visibilityState === "visible") reconcile();
    };
    const interval = window.setInterval(reconcile, 30000);
    window.addEventListener("websocket-connect", reconcile);
    window.addEventListener("inbox-reconcile", reconcile);
    window.addEventListener("focus", reconcile);
    window.addEventListener("online", reconcile);
    document.addEventListener("visibilitychange", whenVisible);

    return () => {
      active = false;
      controller.current = null;
      window.clearTimeout(retryTimer);
      window.clearInterval(interval);
      window.removeEventListener("websocket-connect", reconcile);
      window.removeEventListener("inbox-reconcile", reconcile);
      window.removeEventListener("focus", reconcile);
      window.removeEventListener("online", reconcile);
      document.removeEventListener("visibilitychange", whenVisible);
    };
  }, [scopeThreadId, personalInboxId, legacy]);

  useEffect(() => {
    if (readyThreadId) controller.current?.reconcile();
  }, [readyThreadId, personalInboxId, legacy]);

  useEffect(() => {
    // Do not replay the last notification when selecting or reopening a
    // conversation. The initial detail reconciliation handles that catch-up.
    if (lastNotification.current === notification) return;
    lastNotification.current = notification;
    controller.current?.notify(notification);
  }, [notification]);
}
